import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { osd as OpenSeadragon } from "../../utils/openseadragon-opacity-slider";
import { TileSource } from "openseadragon";
import {
  EventPayload,
  CanvasDataEvent,
  EventTypes,
} from "../../providers/types";
import { useWindowDimensions } from "../../hooks/useWindowDimensions";
import { useAppContext } from "../../contexts/appContext";
import {
  CanvasMode,
  MeetingRole,
  NavEvents,
  State,
} from "../../contexts/types";
import { useOpenSeadragon } from "../../hooks/useOpenSeadragon";
import { BoardImage } from "../../types";
import { isIOS, isIOS13 } from "react-device-detect";
import { VALID_KEYS_WITH_ZOOM } from "../../constants";
import { exploreToggleEventCheck } from "../../utils/eventUtils";
import classnames from "classnames";
import { getTileSources, getIosRender } from "../../utils/openSeadragonUtils";
import EditIIIFViewer from "./editIIIFViewer";
import { consoleNonProd } from "../../utils/utilityBeltUtils";
import variables from "../../styles/variables.scss";

type IIIFViewerProps = {
  tileSources: BoardImage[];
  leader: boolean;
  events?: CanvasDataEvent[];
  handleRefetchBoards?: () => void;
  mode: CanvasMode;
  editBoardIIIF: boolean;
  setIIIFRemoved: (removed: boolean) => void;
  sendEvent?:
    | ((eventPayload: EventPayload, appState: State) => void)
    | undefined;
};

/** IIIFViewer Component, uses a ref to drop out of react.
 * @param {IIIFViewerProps} props
 * @prop {BoardImage[]} tileSources - Array of image sources to be used
 * @prop {boolean} leader - Whether or not the user should emit their canvas actions
 * @prop {CanvasDataEvent[]} events - List of app events from the DataEventsProvider, defaults to an empty array
 */
const IIIFViewer: React.FC<IIIFViewerProps> = ({
  tileSources,
  leader,
  events = [],
  handleRefetchBoards,
  mode,
  editBoardIIIF,
  setIIIFRemoved,
  sendEvent,
}: IIIFViewerProps) => {
  const hasLayers = tileSources.some(
    (source) => source.boardImageLayers?.length > 0
  );
  const ref: React.Ref<HTMLDivElement> = useRef(null);
  const shouldDisableSliderRef = useRef(false);
  const { height } = useWindowDimensions();
  const { generateEventHandlers, meetingRole, state, isInstructorOrModerator } =
    useAppContext();
  const stateRef = useRef(state);
  const prevExploreModeRef = useRef(state.exploreMode);
  const {
    addHandlers,
    onFitCanvas,
    onZoomIn,
    onZoomOut,
    onKeyboard,
    sendCanvasClickEvent,
    handleCanvasMovement,
  } = useOpenSeadragon(leader, events, sendEvent);
  const isMouseNavEnabled = useMemo(
    () => (leader || state.exploreMode) && !state.spotlightInstructor,
    [leader, state]
  );
  const [osd, setOsd] = useState<OpenSeadragon.Viewer>(undefined);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    const wasInExploreMode = prevExploreModeRef.current;
    prevExploreModeRef.current = state.exploreMode;

    if (!wasInExploreMode || state.exploreMode) return;
    if (!leader || !osd || !hasLayers || !sendEvent) return;

    const layerOpacityData = Array.from(
      { length: osd.world.getItemCount() },
      (_, i) => ({
        layerIndex: i,
        opacity: osd.world.getItemAt(i)?.getOpacity() ?? 1,
      })
    );
    sendEvent(
      {
        type: EventTypes.LAYER_OPACITY_CHANGE,
        leader: false,
        layerOpacityData,
      },
      stateRef.current
    );
  }, [state.exploreMode, leader, osd, hasLayers, sendEvent]);

  const onOpacityChange = useCallback(
    (opacity: number, layerIndex: number) => {
      if (leader && sendEvent) {
        sendEvent(
          {
            type: EventTypes.LAYER_OPACITY_CHANGE,
            leader,
            layerOpacityData: [{ layerIndex, opacity }],
          },
          stateRef.current
        );
      }
    },
    [leader, sendEvent]
  );

  // Pass ref to useEffect, canvas for IIIF viewer will be dropped underneath the ref.
  useEffect(() => {
    const gottenTileSources = getTileSources(tileSources);
    const useIosCanvas = getIosRender(gottenTileSources as any[]);
    const isIos = isIOS || isIOS13;
    const initOsd = OpenSeadragon({
      id: "openseadragon",
      element: ref.current !== null ? ref.current : undefined,
      tileSources: hasLayers ? undefined : gottenTileSources,
      // useCanvas: true,
      // collectionMode "FALSE" allows for custom placement based on x, y, width or height values
      collectionMode: !hasLayers,
      collectionLayout: "horizontal",
      collectionRows:
        tileSources?.length > 7 ? Math.ceil(tileSources?.length / 8) : 1,
      visibilityRatio: 0.05,
      constrainDuringPan: true,
      showHomeControl: false,
      showZoomControl: false,
      showRotationControl: false,
      showFullPageControl: false,
      mouseNavEnabled: isMouseNavEnabled ? 1 : 0,
      minZoomImageRatio: 0.05,
      defaultZoomLevel: tileSources?.length > 2 ? 0 : 0.0006,
      maxZoomPixelRatio: hasLayers ? 20 : 1.1,
      iOSDevice: isIos,
      // use canvas unless it's ios and there is a straight image (non manifest.json)
      useCanvas: isIos && !useIosCanvas ? false : true,
    });

    initOsd.addHandler("canvas-key", function (event) {
      const keyEvent: KeyboardEvent = event.originalEvent as KeyboardEvent;
      if (VALID_KEYS_WITH_ZOOM.indexOf(keyEvent.key) > -1) {
        event.preventDefaultAction = false;
        onKeyboard(initOsd, state);
      } else {
        event.preventDefaultAction = true;
      }
    });

    initOsd.addHandler("canvas-drag", function (event) {
      if (isMouseNavEnabled) {
        ref.current.classList.add("iiif-viewer__iiif-drag");
      }
    });

    initOsd.addHandler("canvas-drag-end", function (event) {
      ref.current.classList.remove("iiif-viewer__iiif-drag");
    });

    if (hasLayers) {
      const sortedLayers = tileSources[0].boardImageLayers.sort(
        (a, b) => a.order - b.order
      );
      const layerSources = getTileSources(sortedLayers) as TileSource[];

      // OSD's addTiledImage needs a URL string or typed config object ({url, type}).
      // getTileSources spreads the full domain object, which OSD can't type-detect from.
      // Extract just the URL string, or pass the image config object as-is.
      const toTileSourceValue = (source: any) =>
        source?.url ? source : source?.tileSource;

      const validLayers = layerSources
        .map((source, i) => ({
          layer: sortedLayers[i],
          tileSourceValue: toTileSourceValue(source),
        }))
        .filter(({ tileSourceValue }) => !!tileSourceValue);

      const baseTileSourceValue = toTileSourceValue(gottenTileSources[0]);

      const totalImages = validLayers.length + (baseTileSourceValue ? 1 : 0);
      let imagesLoaded = 0;
      const onImageLoaded = () => {
        imagesLoaded++;
        if (imagesLoaded === totalImages) {
          initOsd.viewport.fitBounds(initOsd.world.getHomeBounds(), true);
        }
      };

      // Add base image first so world[0] matches layerInformation[0].
      // The opacity slider maps layerInformation[i] → world.getItemAt(i) by index.
      if (baseTileSourceValue) {
        initOsd.addTiledImage({
          tileSource: baseTileSourceValue,
          opacity: 1,
          x: 0,
          y: 0,
          width: 1,
          success: function (event) {
            // @ts-expect-error
            event.item.setPosition(new OpenSeadragon.Point(0, 0), true);
            onImageLoaded();
          },
        });
      }

      // Layers are added after base (world[1], world[2], …) to match layerInformation order.
      validLayers.forEach(({ tileSourceValue }) => {
        initOsd.addTiledImage({
          tileSource: tileSourceValue,
          x: 0,
          y: 0,
          width: 1,
          opacity: 0,
          success: function (event) {
            // @ts-expect-error
            event.item.setPosition(new OpenSeadragon.Point(0, 0), true);
            onImageLoaded();
          },
        });
      });

      // source must be the URL string that matches item.source.url in OSD's world.
      const toSourceUrl = (v: any) =>
        typeof v === "string" ? v : v?.url ?? v?.tileSource ?? "";

      const layers = validLayers.map(({ tileSourceValue, layer }) => ({
        source: toSourceUrl(tileSourceValue),
        layerName: layer.imageUpload?.caption ?? "",
        includeSlider: true,
      }));

      initOsd.opacityslider({
        sortable: false,
        showLayerPicker: false,
        showNumberInput: false,
        showRemove: false,
        showlayerInformation: false,
        layerInformation: [
          {
            layerName: gottenTileSources[0].imageUpload?.caption ?? "",
            source: toSourceUrl(baseTileSourceValue),
            includeSlider: true,
          },
          ...layers,
        ],
        onMarkupBuilt: () => {
          document
            .querySelectorAll<HTMLInputElement>(
              "[data-osdos='layer-opacity-slider']"
            )
            .forEach((el) => {
              el.disabled = shouldDisableSliderRef.current;
            });
        },
        onChange: onOpacityChange,
      });
    }

    leader && addHandlers(initOsd, state);
    setOsd(initOsd);
    return () => {
      initOsd.destroy();
    };
    /* eslint-disable react-hooks/exhaustive-deps */
    // including leader and exploreMode in the dependency array caused the OSD
    // to reinitialize every time one of those vars updated, so they had to be removed.
  }, [tileSources, ref]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // bug fix for moderators coming back into focus and still having canvas controls
  useEffect(() => {
    // without the timeout parts of the OSD are undefined and the app blows up
    if (!osd) {
      return;
    }
    // without the try catches it blows up (see useOpenSeaDragon handleCanvasMovement)
    if (osd) {
      try {
        osd.setMouseNavEnabled(isMouseNavEnabled);
      } catch (e) {
        consoleNonProd("Error updating mouse navigation", e);
      }
    }
  }, [
    osd,
    state.exploreMode,
    meetingRole,
    state.spotlightInstructor,
    isMouseNavEnabled,
  ]);

  // Set event handlers for canvas button interactions via slide manager
  useEffect(() => {
    generateEventHandlers({
      [NavEvents.FIT]: (fitState) => {
        if (hasLayers && osd) {
          osd.viewport.fitBoundsWithConstraints(
            osd.world.getHomeBounds() as OpenSeadragon.Rect
          );
          if (leader && sendEvent) {
            sendEvent({ type: EventTypes.HOME, leader }, fitState);
          }
        } else {
          onFitCanvas(osd, fitState);
        }
      },
      [NavEvents.ZOOM_IN]: (state) => onZoomIn(osd, state),
      [NavEvents.ZOOM_OUT]: (state) => onZoomOut(osd, state),
    });
  }, [
    generateEventHandlers,
    onFitCanvas,
    onZoomIn,
    onZoomOut,
    osd,
    state,
    hasLayers,
    leader,
    sendEvent,
  ]);

  useEffect(() => {
    const viewport = osd?.viewport;
    if (viewport) {
      if (events?.length && !state.exploreMode) {
        handleCanvasMovement(osd, state);
      }
    }
  }, [osd, events, leader, state.exploreMode, handleCanvasMovement, state]);

  useEffect(() => {
    if (!osd || !hasLayers || !events?.length) return;
    const lastEvent = events[events.length - 1];
    if (lastEvent.isSelf) return;
    const eventPayload: EventPayload = JSON.parse(lastEvent.event);
    if (
      eventPayload.type !== EventTypes.LAYER_OPACITY_CHANGE ||
      !eventPayload.layerOpacityData ||
      eventPayload.exploreMode
    )
      return;

    for (const { layerIndex, opacity } of eventPayload.layerOpacityData) {
      const item = osd.world.getItemAt(layerIndex);
      if (item) item.setOpacity(opacity);
      const rangeInput = document.querySelector<HTMLInputElement>(
        `[data-osdos='layer-opacity-slider'][data-osd-index='${layerIndex}']`
      );
      if (rangeInput) rangeInput.value = String(opacity * 100);
    }
  }, [events, osd, hasLayers]);

  useEffect(() => {
    if (!hasLayers) return;
    let shouldDisable = !state.exploreMode && !isInstructorOrModerator;
    if (mode !== CanvasMode.PRESENTATION) shouldDisable = false;
    shouldDisableSliderRef.current = shouldDisable;
    document
      .querySelectorAll<HTMLInputElement>("[data-osdos='layer-opacity-slider']")
      .forEach((el) => {
        el.disabled = shouldDisable;
      });
  }, [
    state.exploreMode,
    meetingRole,
    osd,
    hasLayers,
    isInstructorOrModerator,
    mode,
  ]);

  // Emit event when exploreMode updates
  useEffect(() => {
    const viewport = osd?.viewport;
    // exploreMode remove from the 115 if check because we only care about the events exploreMode and that
    // is checked below on 122 (as of 10/17/2022)
    if (events?.length && viewport) {
      const canvasDataEv = events[events.length - 1];
      const eventPayload: EventPayload = JSON.parse(canvasDataEv.event);

      // When explore mode is turned off, leader should emit their location
      if (
        leader &&
        canvasDataEv.isSelf &&
        exploreToggleEventCheck(eventPayload).isExploreEvent &&
        !exploreToggleEventCheck(eventPayload).exploreModeValue
      ) {
        setTimeout(() => {
          const center = osd.viewport.getCenter(true);
          const viewportZoom = viewport.getZoom(true);
          sendCanvasClickEvent(center, viewportZoom, state);
        }, 300);
      }
      // on replay we don't have an instructor to ask for their view so
      // we have to cycle back through the events to find one that has
      // IIIF view attached to it and then we set our replay view to that
      if (
        mode === CanvasMode.REPLAY &&
        exploreToggleEventCheck(eventPayload).isExploreEvent &&
        !exploreToggleEventCheck(eventPayload).exploreModeValue
      ) {
        for (let i = events.length - 1; i >= 0; i--) {
          const eventPayload: EventPayload = JSON.parse(canvasDataEv.event);
          if (eventPayload.center && eventPayload.zoom) {
            viewport.zoomTo(eventPayload.zoom, eventPayload.center);
            break;
          }
        }
      }
    }
  }, [
    state.exploreMode,
    events,
    osd,
    leader,
    sendCanvasClickEvent,
    mode,
    state,
    state.spotlightInstructor,
  ]);

  // Update the handlers on state updates
  useEffect(() => {
    if (osd && leader) {
      addHandlers(osd, state);
    }
  }, [addHandlers, leader, osd, state]);

  function flexFinder(): number {
    const length = tileSources.length;
    let returnFlex = 0;
    if (length === 1) {
      returnFlex = 50;
    } else if (length === 2) {
      returnFlex = 40;
    } else if (length === 3) {
      returnFlex = 30;
    } else if (length === 4) {
      returnFlex = 20;
    } else if (length === 5) {
      returnFlex = 16.6;
    } else if (length >= 6) {
      returnFlex = 14.5;
    }
    return returnFlex;
  }

  const handleDeleteBoardComplete = (currentVersion: number) => {
    if (currentVersion) {
      // send version update event
      const event: EventPayload = {
        type: EventTypes.PRESENTATION_VERSION_CHANGE,
        presentationVersion: currentVersion,
        leader: true,
      };

      sendEvent(event, state);
    }

    handleRefetchBoards();
  };

  return (
    <Fragment>
      <div
        id="iiif-editor"
        className="iiif-editor"
        style={{
          visibility: editBoardIIIF ? "visible" : "hidden",
          display: editBoardIIIF ? "flex" : "none",
          justifyContent: tileSources?.length > 2 ? "space-around" : "center",
        }}
      >
        {tileSources?.map((tile, index) => {
          return (
            <EditIIIFViewer
              key={index}
              tileSources={tileSources}
              inputIndex={index}
              flexBasis={flexFinder()}
              handleDeleteBoardComplete={handleDeleteBoardComplete}
              setIIIFRemoved={setIIIFRemoved}
            />
          );
        })}
      </div>
      <div
        id="openseadragon"
        className={classnames(`app__iiif-viewer iiif-viewer`, {
          "iiif-viewer__iiif-grabbable": isMouseNavEnabled,
          "iiif-viewer__blur": state.spotlightInstructor,
        })}
        ref={ref}
        style={{
          height: document?.documentElement?.style.getPropertyValue(
            "--mobile-available-screen"
          )
            ? document?.documentElement?.style.getPropertyValue(
                "--mobile-available-screen"
              )
            : "100vh",
          overflow: "hidden",
          visibility: editBoardIIIF ? "hidden" : "visible",
        }}
      />
      {hasLayers && (
        <div
          id="opacitySliderWrapper"
          style={{
            position: "absolute",
            bottom: "64px",
            left: "8px",
            backgroundColor: variables.backgroundCanvas,
            padding: "8px",
            opacity: "75%",
            borderRadius: "4px",
          }}
        />
      )}
    </Fragment>
  );
};

export default IIIFViewer;
